import React, { useState, useMemo, useCallback } from "react";
import { Eyebrow } from "../atoms/eyebrow";
import { DisplayHeading } from "../atoms/display-heading";
import { Badge } from "../atoms/badge";
import { SearchInput } from "../molecules/search-input";

interface Lesson {
	name: string;
	filename: string;
	description: string;
	code?: string;
	level: "Beginner" | "Intermediate" | "Advanced" | "Expert";
	category: string;
	subcategory?: string;
	tags: string[];
	estimatedTime: string;
}

interface LessonCategory {
	name: string;
	description: string;
	subcategories?: string[];
}

interface LessonsProps {
	onLoadLesson: (code: string, title: string) => void;
	isEmbedded?: boolean;
	hasWalkthrough?: (filename: string) => boolean;
	onStartWalkthrough?: (filename: string) => void;
}

// Define lesson categories with subcategories
const lessonCategories: Record<string, LessonCategory> = {
	Fundamentals: {
		name: "Python Fundamentals",
		description: "Core concepts every Python programmer needs",
		subcategories: [
			"Getting Started",
			"Variables & Data Types",
			"Basic Operations",
			"Input/Output",
		],
	},
	"Control Flow": {
		name: "Control Flow",
		description: "Making decisions and repeating actions",
		subcategories: ["Conditionals", "Loops", "Error Handling"],
	},
	"Data Structures": {
		name: "Data Structures",
		description: "Organizing and managing data",
		subcategories: ["Lists", "Dictionaries", "Sets", "Tuples", "Strings"],
	},
	Functions: {
		name: "Functions & Modules",
		description: "Organizing code and reusability",
		subcategories: [
			"Basic Functions",
			"Advanced Functions",
			"Modules",
			"Decorators",
		],
	},
	"Object-Oriented": {
		name: "Object-Oriented Programming",
		description: "Classes, objects, and OOP concepts",
		subcategories: [
			"Classes & Objects",
			"Inheritance",
			"Polymorphism",
			"Encapsulation",
		],
	},
	"File Operations": {
		name: "File & Data Operations",
		description: "Working with files and external data",
		subcategories: ["File I/O", "CSV Data", "JSON", "Database Basics"],
	},
	Libraries: {
		name: "Popular Libraries",
		description: "Essential Python libraries and frameworks",
		subcategories: [
			"Data Analysis",
			"Visualization",
			"Web Scraping",
			"GUI Development",
		],
	},
	Advanced: {
		name: "Advanced Topics",
		description: "Advanced Python concepts and techniques",
		subcategories: [
			"Generators",
			"Context Managers",
			"Metaclasses",
			"Concurrency",
		],
	},
};

// Define comprehensive lessons data
const lessonsData: Omit<Lesson, "code">[] = [
	// FUNDAMENTALS
	// Getting Started
	{
		name: "Hello World",
		filename: "hello_world.py",
		description: "Your very first Python program",
		level: "Beginner",
		category: "Fundamentals",
		subcategory: "Getting Started",
		tags: ["basics", "print", "first-program"],
		estimatedTime: "2 min",
	},
	{
		name: "Your First Program",
		filename: "beginner_hello.py",
		description: "Gentle introduction with detailed explanations",
		level: "Beginner",
		category: "Fundamentals",
		subcategory: "Getting Started",
		tags: ["basics", "variables", "comments"],
		estimatedTime: "5 min",
	},
	{
		name: "Comments & Documentation",
		filename: "comments_demo.py",
		description: "Writing clear comments and documentation",
		level: "Beginner",
		category: "Fundamentals",
		subcategory: "Getting Started",
		tags: ["comments", "documentation", "best-practices"],
		estimatedTime: "3 min",
	},

	// Variables & Data Types
	{
		name: "Variables Basics",
		filename: "variables_basics.py",
		description: "Understanding variables and naming rules",
		level: "Beginner",
		category: "Fundamentals",
		subcategory: "Variables & Data Types",
		tags: ["variables", "naming", "assignment"],
		estimatedTime: "4 min",
	},
	{
		name: "Numbers & Math",
		filename: "beginner_math.py",
		description: "Working with integers, floats, and math operations",
		level: "Beginner",
		category: "Fundamentals",
		subcategory: "Variables & Data Types",
		tags: ["numbers", "math", "operators"],
		estimatedTime: "6 min",
	},
	{
		name: "Strings Fundamentals",
		filename: "strings_basics.py",
		description: "Text manipulation and string methods",
		level: "Beginner",
		category: "Fundamentals",
		subcategory: "Variables & Data Types",
		tags: ["strings", "text", "methods"],
		estimatedTime: "7 min",
	},
	{
		name: "Boolean Logic",
		filename: "boolean_basics.py",
		description: "True, False, and logical operations",
		level: "Beginner",
		category: "Fundamentals",
		subcategory: "Variables & Data Types",
		tags: ["boolean", "logic", "true-false"],
		estimatedTime: "5 min",
	},

	// Basic Operations
	{
		name: "Basic Math Operations",
		filename: "basic_math.py",
		description: "Advanced mathematical operations and functions",
		level: "Intermediate",
		category: "Fundamentals",
		subcategory: "Basic Operations",
		tags: ["math", "calculations", "functions"],
		estimatedTime: "8 min",
	},
	{
		name: "String Operations",
		filename: "string_operations.py",
		description: "String formatting, slicing, and manipulation",
		level: "Intermediate",
		category: "Fundamentals",
		subcategory: "Basic Operations",
		tags: ["strings", "formatting", "slicing"],
		estimatedTime: "10 min",
	},

	// Input/Output
	{
		name: "User Input",
		filename: "user_input.py",
		description: "Getting input from users and making programs interactive",
		level: "Beginner",
		category: "Fundamentals",
		subcategory: "Input/Output",
		tags: ["input", "interactive", "user-interface"],
		estimatedTime: "5 min",
	},
	{
		name: "Print Formatting",
		filename: "print_formatting.py",
		description: "Advanced printing and output formatting techniques",
		level: "Intermediate",
		category: "Fundamentals",
		subcategory: "Input/Output",
		tags: ["print", "formatting", "f-strings"],
		estimatedTime: "8 min",
	},

	// CONTROL FLOW
	// Conditionals
	{
		name: "If Statements",
		filename: "if_statements.py",
		description: "Making decisions with if, elif, and else",
		level: "Beginner",
		category: "Control Flow",
		subcategory: "Conditionals",
		tags: ["if", "elif", "else", "conditionals"],
		estimatedTime: "6 min",
	},
	{
		name: "Comparison Operators",
		filename: "comparison_operators.py",
		description: "Comparing values with ==, !=, <, >, <=, >=",
		level: "Beginner",
		category: "Control Flow",
		subcategory: "Conditionals",
		tags: ["comparison", "operators", "logic"],
		estimatedTime: "5 min",
	},
	{
		name: "Logical Operators",
		filename: "logical_operators.py",
		description: "Combining conditions with and, or, not",
		level: "Intermediate",
		category: "Control Flow",
		subcategory: "Conditionals",
		tags: ["and", "or", "not", "logic"],
		estimatedTime: "7 min",
	},

	// Loops
	{
		name: "For Loops",
		filename: "for_loops.py",
		description: "Repeating actions with for loops",
		level: "Beginner",
		category: "Control Flow",
		subcategory: "Loops",
		tags: ["for", "loops", "iteration"],
		estimatedTime: "8 min",
	},
	{
		name: "While Loops",
		filename: "while_loops.py",
		description: "Conditional repetition with while loops",
		level: "Beginner",
		category: "Control Flow",
		subcategory: "Loops",
		tags: ["while", "loops", "conditions"],
		estimatedTime: "7 min",
	},
	{
		name: "Loop Control",
		filename: "loop_control.py",
		description: "Break, continue, and else in loops",
		level: "Intermediate",
		category: "Control Flow",
		subcategory: "Loops",
		tags: ["break", "continue", "loop-control"],
		estimatedTime: "9 min",
	},

	// DATA STRUCTURES
	// Lists
	{
		name: "Lists and Loops",
		filename: "lists_and_loops.py",
		description: "Working with data collections and iteration",
		level: "Intermediate",
		category: "Data Structures",
		subcategory: "Lists",
		tags: ["lists", "loops", "iteration"],
		estimatedTime: "10 min",
	},
	{
		name: "List Methods",
		filename: "list_methods.py",
		description: "Adding, removing, and modifying list elements",
		level: "Intermediate",
		category: "Data Structures",
		subcategory: "Lists",
		tags: ["lists", "methods", "append", "remove"],
		estimatedTime: "12 min",
	},
	{
		name: "List Comprehensions",
		filename: "list_comprehensions.py",
		description: "Creating lists with concise syntax",
		level: "Advanced",
		category: "Data Structures",
		subcategory: "Lists",
		tags: ["list-comprehension", "advanced", "syntax"],
		estimatedTime: "15 min",
	},

	// Dictionaries
	{
		name: "Dictionary Basics",
		filename: "dict_basics.py",
		description: "Key-value pairs and dictionary operations",
		level: "Intermediate",
		category: "Data Structures",
		subcategory: "Dictionaries",
		tags: ["dictionaries", "key-value", "mapping"],
		estimatedTime: "10 min",
	},
	{
		name: "Advanced Dictionaries",
		filename: "advanced_dictionaries.py",
		description: "Dictionary methods and advanced usage",
		level: "Advanced",
		category: "Data Structures",
		subcategory: "Dictionaries",
		tags: ["dictionaries", "methods", "advanced"],
		estimatedTime: "12 min",
	},

	// FUNCTIONS
	// Basic Functions
	{
		name: "Creating Functions",
		filename: "functions_demo.py",
		description: "Organizing code with functions and parameters",
		level: "Intermediate",
		category: "Functions",
		subcategory: "Basic Functions",
		tags: ["functions", "parameters", "return"],
		estimatedTime: "12 min",
	},
	{
		name: "Function Parameters",
		filename: "function_parameters.py",
		description: "Default values, *args, and **kwargs",
		level: "Advanced",
		category: "Functions",
		subcategory: "Advanced Functions",
		tags: ["parameters", "args", "kwargs"],
		estimatedTime: "15 min",
	},

	// LIBRARIES
	// Data Analysis
	{
		name: "Working with Data",
		filename: "simple_pandas.py",
		description: "Data analysis with pandas library",
		level: "Advanced",
		category: "Libraries",
		subcategory: "Data Analysis",
		tags: ["pandas", "data-analysis", "csv"],
		estimatedTime: "20 min",
	},

	// Visualization
	{
		name: "Drawing Graphs",
		filename: "simple_plotting.py",
		description: "Create beautiful visualizations with matplotlib",
		level: "Advanced",
		category: "Libraries",
		subcategory: "Visualization",
		tags: ["matplotlib", "plotting", "visualization"],
		estimatedTime: "18 min",
	},

	// ADVANCED
	{
		name: "Sorting Algorithms",
		filename: "bubble_sort.py",
		description: "Understanding sorting algorithms and complexity",
		level: "Expert",
		category: "Advanced",
		subcategory: "Algorithms",
		tags: ["sorting", "algorithms", "bubble-sort"],
		estimatedTime: "25 min",
	},
	{
		name: "Data Structures Overview",
		filename: "data_structures.py",
		description: "Overview of various data structures in Python",
		level: "Advanced",
		category: "Data Structures",
		subcategory: "Advanced",
		tags: ["data-structures", "overview", "performance"],
		estimatedTime: "15 min",
	},
];

const Lessons: React.FC<LessonsProps> = ({
	onLoadLesson,
	isEmbedded = false,
	hasWalkthrough,
	onStartWalkthrough,
}) => {
	const [lessons, setLessons] = useState<Lesson[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(
		null,
	);
	const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
		new Set(),
	);
	const [loadedFiles, setLoadedFiles] = useState<Set<string>>(new Set());

	// Lazy load lesson content when needed
	const loadLessonContent = useCallback(
		async (lesson: Omit<Lesson, "code">) => {
			if (loadedFiles.has(lesson.filename)) {
				return lessons.find((l) => l.filename === lesson.filename);
			}

			try {
				const response = await fetch(`/examples/${lesson.filename}`);
				if (response.ok) {
					const code = await response.text();
					const fullLesson: Lesson = { ...lesson, code };

					setLessons((prev) => {
						const filtered = prev.filter((l) => l.filename !== lesson.filename);
						return [...filtered, fullLesson];
					});

					setLoadedFiles(
						(prev) => new Set(Array.from(prev).concat([lesson.filename])),
					);
					return fullLesson;
				}
			} catch (error) {
				console.warn(`Failed to load lesson: ${lesson.filename}`, error);
			}
			return null;
		},
		[lessons, loadedFiles],
	);

	const handleLoadLesson = async (lessonData: Omit<Lesson, "code">) => {
		// Prefer the guided walkthrough if one exists for this lesson.
		if (hasWalkthrough?.(lessonData.filename) && onStartWalkthrough) {
			onStartWalkthrough(lessonData.filename);
			return;
		}
		const lesson = await loadLessonContent(lessonData);
		if (lesson && lesson.code) {
			onLoadLesson(lesson.code, lesson.name);
		}
	};

	// Filter lessons based on search and selections
	const filteredLessons = useMemo(() => {
		let filtered = lessonsData;

		// Filter by search term
		if (searchTerm) {
			const search = searchTerm.toLowerCase();
			filtered = filtered.filter(
				(lesson) =>
					lesson.name.toLowerCase().includes(search) ||
					lesson.description.toLowerCase().includes(search) ||
					lesson.tags.some((tag) => tag.toLowerCase().includes(search)) ||
					lesson.category.toLowerCase().includes(search) ||
					lesson.subcategory?.toLowerCase().includes(search),
			);
		}

		// Filter by category
		if (selectedCategory) {
			filtered = filtered.filter(
				(lesson) => lesson.category === selectedCategory,
			);
		}

		// Filter by subcategory
		if (selectedSubcategory) {
			filtered = filtered.filter(
				(lesson) => lesson.subcategory === selectedSubcategory,
			);
		}

		return filtered;
	}, [searchTerm, selectedCategory, selectedSubcategory]);

	// Group lessons by category and subcategory
	const groupedLessons = useMemo(() => {
		const grouped: Record<string, Record<string, Lesson[]>> = {};

		filteredLessons.forEach((lesson) => {
			if (!grouped[lesson.category]) {
				grouped[lesson.category] = {};
			}

			const subcategory = lesson.subcategory || "General";
			if (!grouped[lesson.category][subcategory]) {
				grouped[lesson.category][subcategory] = [];
			}

			grouped[lesson.category][subcategory].push(lesson as Lesson);
		});

		return grouped;
	}, [filteredLessons]);

	const getLevelStyles = (level: string) => {
		const styles = {
			Beginner: {
				badge:
					"bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100",
				text: "text-emerald-600 dark:text-emerald-400",
			},
			Intermediate: {
				badge: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100",
				text: "text-blue-600 dark:text-blue-400",
			},
			Advanced: {
				badge:
					"bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100",
				text: "text-purple-600 dark:text-purple-400",
			},
			Expert: {
				badge:
					"bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100",
				text: "text-orange-600 dark:text-orange-400",
			},
		};
		return (
			styles[level as keyof typeof styles] || {
				badge: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100",
				text: "text-gray-600 dark:text-gray-400",
			}
		);
	};

	const getCategoryStyles = (categoryName: string) => {
		const styles = {
			Fundamentals: "text-emerald-700 dark:text-emerald-400",
			"Control Flow": "text-blue-700 dark:text-blue-400",
			"Data Structures": "text-purple-700 dark:text-purple-400",
			Functions: "text-indigo-700 dark:text-indigo-400",
			"Object-Oriented": "text-pink-700 dark:text-pink-400",
			"File Operations": "text-yellow-700 dark:text-yellow-400",
			Libraries: "text-teal-700 dark:text-teal-400",
			Advanced: "text-orange-700 dark:text-orange-400",
		};
		return (
			styles[categoryName as keyof typeof styles] ||
			"text-gray-700 dark:text-gray-400"
		);
	};

	const toggleCategory = (category: string) => {
		const newExpanded = new Set(Array.from(expandedCategories));
		if (expandedCategories.has(category)) {
			newExpanded.delete(category);
		} else {
			newExpanded.add(category);
		}
		setExpandedCategories(newExpanded);
	};

	const clearFilters = () => {
		setSearchTerm("");
		setSelectedCategory(null);
		setSelectedSubcategory(null);
	};

	if (!isEmbedded) {
		return null;
	}

	return (
		<div className="flex flex-col h-full bg-white dark:bg-[#0c0c0e]">
			{/* Header */}
			<div className="flex-shrink-0 px-3.5 pt-3 pb-2.5 border-b border-gray-200/80 dark:border-white/[0.06]">
				<div className="flex items-baseline justify-between mb-2">
					<div className="flex items-baseline gap-2">
						<DisplayHeading size="md">Lessons</DisplayHeading>
						<Eyebrow className="!text-[9px] !tracking-[0.18em]">
							{filteredLessons.length}
						</Eyebrow>
					</div>
				</div>
				<p className="text-[11px] text-gray-500 dark:text-gray-500 mb-2.5">
					Interactive learning modules.
				</p>

				<SearchInput
					placeholder="Search lessons…"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					hasValue={!!searchTerm}
					onClear={() => setSearchTerm("")}
				/>

				{(selectedCategory || selectedSubcategory || searchTerm) && (
					<div className="mt-2 flex flex-wrap gap-1">
						{selectedCategory && (
							<Badge tone="accent" className="normal-case !text-[10px] !tracking-tight !px-2">
								{selectedCategory}
								<button
									onClick={() => setSelectedCategory(null)}
									className="hover:opacity-70 -mr-0.5"
									aria-label="Clear category"
								>
									×
								</button>
							</Badge>
						)}
						{selectedSubcategory && (
							<Badge tone="neutral" className="normal-case !text-[10px] !tracking-tight !px-2">
								{selectedSubcategory}
								<button
									onClick={() => setSelectedSubcategory(null)}
									className="hover:opacity-70 -mr-0.5"
									aria-label="Clear subcategory"
								>
									×
								</button>
							</Badge>
						)}
						<button
							onClick={clearFilters}
							className="text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 px-1"
						>
							Clear all
						</button>
					</div>
				)}
			</div>

			{/* Lessons Content */}
			<div className="flex-1 overflow-y-auto">
				<div className="p-2">
						{Object.keys(groupedLessons).length === 0 ? (
							<div className="text-center py-8">
								<div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-3">
									<svg
										className="w-6 h-6 text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
										></path>
									</svg>
								</div>
								<p className="text-gray-600 dark:text-gray-400 text-sm">
									No lessons found
								</p>
								<p className="text-gray-500 dark:text-gray-500 text-xs mt-1">
									Try adjusting your search or filters
								</p>
							</div>
						) : (
							<div className="space-y-4">
								{Object.entries(groupedLessons).map(
									([categoryName, subcategories], categoryIndex) => {
										const category = lessonCategories[categoryName];
										const isExpanded = expandedCategories.has(categoryName);
										const categoryStyles = getCategoryStyles(categoryName);

										return (
											<div key={categoryName} className="overflow-hidden">
												{/* Sleek divider between categories */}
												{categoryIndex > 0 && (
													<div className="relative py-3 mb-4">
														<div className="absolute inset-0 flex items-center">
															<div className="w-full border-t border-gray-200 dark:border-gray-600"></div>
														</div>
														<div className="relative flex justify-center">
															<div className="bg-white dark:bg-gray-800 px-3">
																<div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
															</div>
														</div>
													</div>
												)}

												{/* Category Header */}
												<button
													onClick={() => toggleCategory(categoryName)}
													className="w-full text-left px-2.5 py-2 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors duration-150 rounded-md group"
												>
													<div className="flex items-center justify-between">
														<div className="flex-1 min-w-0">
															<div className="flex items-baseline gap-2 mb-0.5">
																<h4
																	className={`text-[13px] font-medium tracking-tight ${categoryStyles}`}
																>
																	{category?.name || categoryName}
																</h4>
																<span className="text-[10px] tabular text-gray-400 dark:text-gray-600">
																	{Object.values(subcategories).flat().length}
																</span>
															</div>
															<p className="text-[11px] text-gray-500 dark:text-gray-500 leading-snug">
																{category?.description}
															</p>
														</div>

														<svg
															className={`ml-2 w-3.5 h-3.5 text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
															fill="none"
															stroke="currentColor"
															strokeWidth="1.7"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="M19 9l-7 7-7-7"
															/>
														</svg>
													</div>
												</button>

												{/* Collapsible Content */}
												<div
													className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}
												>
													<div className="pt-3">
														{Object.entries(subcategories).map(
															([subcategoryName, lessons], subIndex) => (
																<div key={subcategoryName} className="mb-3">
																	{/* Subcategory Header */}
																	<div className="px-2.5 py-1.5 mb-0.5 flex items-center gap-2">
																		<h5 className="eyebrow !text-[9px] !tracking-[0.18em]">
																			{subcategoryName}
																		</h5>
																		<div className="flex-1 h-px bg-gray-100 dark:bg-white/[0.05]"></div>
																	</div>

																	{/* Lessons List */}
																	<div>
																		{lessons.map((lesson, lessonIndex) => {
																			const levelStyles = getLevelStyles(
																				lesson.level,
																			);
																			return (
																				<button
																					key={`${lesson.filename}-${lessonIndex}`}
																					onClick={() =>
																						handleLoadLesson(lesson)
																					}
																					className="w-full text-left px-2.5 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors group"
																				>
																					<div className="flex items-start justify-between gap-2">
																						<div className="flex-1 min-w-0">
																							<div className="flex items-center gap-1.5 mb-0.5">
																								<h6 className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-orange-700 dark:group-hover:text-amber-300 transition-colors">
																									{lesson.name}
																								</h6>
																								{hasWalkthrough?.(lesson.filename) && (
																									<span
																										className="inline-flex items-center gap-0.5 text-[9px] font-medium tracking-tight px-1.5 py-0 rounded border border-orange-200 dark:border-amber-400/30 bg-orange-50 dark:bg-amber-400/10 text-orange-700 dark:text-amber-300"
																										title="Step-by-step guided walkthrough"
																									>
																										<svg className="w-2 h-2" fill="currentColor" viewBox="0 0 8 8" aria-hidden="true">
																											<circle cx="4" cy="4" r="3" />
																										</svg>
																										Guided
																									</span>
																								)}
																								<span
																									className={`chip normal-case !text-[9px] !tracking-tight !px-1.5 !py-0 ${levelStyles.badge}`}
																								>
																									{lesson.level}
																								</span>
																							</div>
																							<p className="text-[11px] text-gray-500 dark:text-gray-500 line-clamp-2 leading-snug mb-1.5">
																								{lesson.description}
																							</p>
																							<div className="flex items-center gap-2">
																								<span className="text-[10px] tabular text-gray-400 dark:text-gray-600 flex items-center gap-1">
																									<svg
																										className="w-2.5 h-2.5"
																										fill="none"
																										stroke="currentColor"
																										strokeWidth="2"
																										viewBox="0 0 24 24"
																									>
																										<path
																											strokeLinecap="round"
																											strokeLinejoin="round"
																											d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
																										/>
																									</svg>
																									{lesson.estimatedTime}
																								</span>
																								<div className="flex gap-1">
																									{lesson.tags
																										.slice(0, 2)
																										.map((tag) => (
																											<span
																												key={tag}
																												className="text-[10px] tracking-tight text-gray-500 dark:text-gray-500"
																											>
																												·{tag}
																											</span>
																										))}
																								</div>
																							</div>
																						</div>

																						<svg
																							className="w-3 h-3 text-gray-300 dark:text-gray-700 mt-1 opacity-0 group-hover:opacity-100 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-all"
																							fill="none"
																							stroke="currentColor"
																							strokeWidth="2"
																							viewBox="0 0 24 24"
																						>
																							<path
																								strokeLinecap="round"
																								strokeLinejoin="round"
																								d="M9 5l7 7-7 7"
																							/>
																						</svg>
																					</div>
																				</button>
																			);
																		})}
																	</div>
																</div>
															),
														)}
													</div>
												</div>
											</div>
										);
									},
								)}
							</div>
						)}
					</div>
			</div>

			{/* Footer Stats */}
			<div className="flex-shrink-0 px-3.5 py-2 border-t border-gray-200/80 dark:border-white/[0.06]">
				<div className="flex items-center justify-between text-[10px] tabular text-gray-500 dark:text-gray-500">
					<span className="tabular">
						<span className="text-gray-900 dark:text-gray-200 font-medium">
							{filteredLessons.length}
						</span>{" "}
						available
					</span>
					<span className="eyebrow !text-[9px] !tracking-[0.16em]">
						Click to expand
					</span>
				</div>
			</div>
		</div>
	);
};

export default Lessons;
